/**
 * Server-agnostic entity-report binder — the ONE binding implementation
 * for report documents (job_report_document MVP).
 *
 * Turns a ReportDocument version's content into a "rendered" document:
 * the same EacoDocumentContent shape, but with `data.bound*` baked into
 * the report element types (job-attachments, logo) plus `{{job.*}}` /
 * `{{report.*}}` placeholder substitution on headings/paragraphs. The
 * @eaco/forms-ui-* renderers read `element.data.bound*` directly.
 *
 * Pure: it takes already-fetched inputs and returns bound content — no
 * DB, no network. All three consumers share it:
 * - apps/graphql `renderedJobReport` (fetches via the ISM bridge, binds)
 * - the PDF path (apps/graphql binds, forms-pdf-service only SSRs)
 * - the web editor preview (binds mockJobReportData() client-side)
 * Deliberately unlike form outputs, there is no PHP twin to keep in sync.
 *
 * Aggregate-ready: the scope carries `entities: []`; single-entity
 * elements bind entities[0], and a future job_list_report_document adds
 * list elements that iterate the array — no signature change.
 */
import type {
  BoundBudget,
  BoundCalendarEventRow,
  BoundCalendarEvents,
  BoundChatMessageRow,
  BoundChatTranscript,
  BoundCheckInRow,
  BoundCheckIns,
  BoundEvent,
  BoundActionItemRow,
  BoundActionItems,
  BoundFinanceLine,
  BoundFinanceList,
  BoundJobAttachmentItem,
  BoundJobAttachments,
  BoundJobField,
  BoundJobNoteRow,
  BoundJobNotes,
  BoundJobTaskRow,
  BoundJobTasks,
  BoundJobTags,
  BoundLogo,
  BoundTagChip,
  BoundWorkLog,
  BoundWorkLogRow,
  DocumentElement,
  EacoDocumentContent,
  ElementSelection,
  JobCoreData,
  RenderedReportContent,
  ReportSelections,
  BoundExternalChatChannel,
} from "./types";
import { JOB_FIELDS, JOB_RICH_TEXT_FIELD_IDS } from "./elementTypes";
import {
  plainTextToHtml,
  renderRichTextHtml,
  richTextToPlainText,
} from "./richTextHtml";

/**
 * The pre-fetched snapshot of one job that the binder consumes. One
 * fetch implementation (apps/graphql fetchJobReportData) produces this;
 * mockJobReportData() fabricates it for the editor preview.
 */
export interface JobReportEntityData {
  entityType: "job";
  job: JobCoreData;
  /** Signed, display-ready attachment tiles (binder filters by selection). */
  attachments: BoundJobAttachmentItem[];
  /** Photos captured in the job's task/form results (image-upload answers). */
  formResultPhotos: BoundJobAttachmentItem[];
  /** Image files in the job's documents drive. */
  drivePhotos: BoundJobAttachmentItem[];
  /**
   * Workflow lifecycle events (created / approved / workflow-stage-
   * priority changes / completed / closed…), pre-labelled and sorted
   * oldest → newest by the fetcher. Same BoundEvent shape the form
   * event-timeline uses, so the renderer is shared.
   */
  events: BoundEvent[];
  /** Work log rows, oldest → newest (fetcher pre-sorts + labels users). */
  workLog: BoundWorkLogRow[];
  /** Check-ins, oldest → newest. */
  checkIns: BoundCheckInRow[];
  /** Scheduled calendar events, oldest → newest. */
  calendarEvents: BoundCalendarEventRow[];
  /**
   * Finance lists keyed by the element family that renders them. Rows
   * are pre-labelled/formatted by the fetcher; the binder only sums
   * totals. Callers without finance access supply empty arrays.
   */
  finance: {
    invoices: BoundFinanceLine[];
    bills: BoundFinanceLine[];
    quotesSubmitted: BoundFinanceLine[];
    quotesReceived: BoundFinanceLine[];
    purchaseOrders: BoundFinanceLine[];
    expenses: BoundFinanceLine[];
  };
  /** Budget rows (null when the job has no budget). */
  budget: BoundBudget | null;
  /** Notes (parents followed by their replies), oldest → newest. */
  notes: BoundJobNoteRow[];
  /** Action items linked to the job (fetcher excludes archived). */
  actionItems: BoundActionItemRow[];
  /** Job tasks with completion state (fetcher excludes archived). */
  tasks: BoundJobTaskRow[];
  /** Tag chips with display colours. */
  tags: BoundTagChip[];
  /** Internal chat messages, oldest → newest (system messages excluded). */
  chatMessages: BoundChatMessageRow[];
  /** Cross-client chats on the job the owner tenant is party to (optional for older callers). */
  externalChats?: BoundExternalChatChannel[];
  owner: {
    name: string | null;
    logoUrl: string | null;
  };
}

/** Aggregate-ready entity scope. MVP binds entities[0]. */
export interface EntityReportScope {
  entityType: "job";
  entities: JobReportEntityData[];
}

export interface RenderEntityReportInput {
  /** Report document name — used for {{report.name}}. */
  reportName: string;
  /** The version content being rendered (template or instance). */
  content: EacoDocumentContent;
  scope: EntityReportScope;
  /**
   * Ephemeral per-element selection overrides (the wizard's one-time
   * run). Merged over each element's persisted data.selection before
   * filtering. "Save to job" bakes the same shape into the instance
   * content instead, so saved reports render with no overrides.
   */
  selections?: ReportSelections | null;
  /** ISO date for {{today}}; callers stamp it (keeps the binder pure). */
  todayFormatted?: string | null;
  /** IANA zone job dates are shown in (the owner's); null uses the runtime's. */
  timeZone?: string | null;
}

/** Bind a report document against an entity scope → rendered content. */
export function renderEntityReport(
  input: RenderEntityReportInput
): RenderedReportContent {
  const content: EacoDocumentContent = {
    elements: Array.isArray(input.content?.elements)
      ? input.content.elements
      : [],
    logic: input.content?.logic,
    responseSets: input.content?.responseSets,
    settings: input.content?.settings,
  };

  const entity = input.scope.entities[0] ?? null;
  const placeholders = buildPlaceholders(input, entity);

  const bound: RenderedReportContent = {
    ...content,
    elements: walkElements(
      content.elements,
      entity,
      input.selections ?? null,
      placeholders,
      input.timeZone ?? null
    ),
  };
  if (entity) {
    bound.boundEntity = {
      entityType: "job",
      domainId: entity.job.domainId,
      oid: entity.job.oid,
      name: entity.job.title ?? entity.job.refId ?? "",
    };
    bound.boundMeta = {
      companyName: entity.owner.name,
      jobName: entity.job.title ?? entity.job.refId ?? null,
    };
  }
  return bound;
}

function walkElements(
  elements: DocumentElement[],
  entity: JobReportEntityData | null,
  selections: ReportSelections | null,
  placeholders: Record<string, string>,
  timeZone: string | null
): DocumentElement[] {
  const out: DocumentElement[] = [];
  for (const original of elements) {
    if (!original || typeof original !== "object") continue;
    const element: DocumentElement = { ...original };
    const data: Record<string, unknown> = { ...(element.data ?? {}) };
    element.data = data;
    const typeId = String(element.typeId ?? "");

    switch (typeId) {
      case "job-attachments":
        data.boundJobAttachments = bindJobAttachments(
          entity,
          data,
          resolveSelection(element.id, data, selections)
        );
        break;
      case "job-field":
        data.boundJobField = bindJobField(entity, data, timeZone);
        break;
      case "job-timeline":
        data.boundEvents = entity ? entity.events : [];
        break;
      case "job-work-log":
        data.boundWorkLog = bindWorkLog(entity);
        break;
      case "job-check-ins":
        data.boundCheckIns = bindCheckIns(entity, data);
        break;
      case "job-calendar-events":
        data.boundCalendarEvents = {
          rows: entity ? entity.calendarEvents : [],
        } as BoundCalendarEvents;
        break;
      case "job-invoices":
        data.boundFinanceList = bindFinanceList(
          entity?.finance.invoices,
          resolveSelection(element.id, data, selections)
        );
        break;
      case "job-bills":
        data.boundFinanceList = bindFinanceList(
          entity?.finance.bills,
          resolveSelection(element.id, data, selections)
        );
        break;
      case "job-quotes-submitted":
        data.boundFinanceList = bindFinanceList(
          entity?.finance.quotesSubmitted,
          resolveSelection(element.id, data, selections)
        );
        break;
      case "job-quotes-received":
        data.boundFinanceList = bindFinanceList(
          entity?.finance.quotesReceived,
          resolveSelection(element.id, data, selections)
        );
        break;
      case "job-purchase-orders":
        data.boundFinanceList = bindFinanceList(
          entity?.finance.purchaseOrders,
          resolveSelection(element.id, data, selections)
        );
        break;
      case "job-expenses":
        data.boundFinanceList = bindFinanceList(
          entity?.finance.expenses,
          resolveSelection(element.id, data, selections)
        );
        break;
      case "job-budget":
        data.boundBudget = entity ? entity.budget : null;
        break;
      case "job-notes":
        data.boundJobNotes = bindJobNotes(
          entity,
          data,
          resolveSelection(element.id, data, selections)
        );
        break;
      case "job-action-items":
        data.boundActionItems = bindActionItems(
          entity,
          data,
          resolveSelection(element.id, data, selections)
        );
        break;
      case "job-tasks":
        data.boundJobTasks = {
          rows: entity ? entity.tasks : [],
        } as BoundJobTasks;
        break;
      case "job-tags":
        data.boundJobTags = {
          chips: entity ? entity.tags : [],
        } as BoundJobTags;
        break;
      case "job-chat-transcript":
        data.boundChatTranscript = bindChatTranscript(
          entity,
          data,
          resolveSelection(element.id, data, selections)
        );
        break;
      case "logo":
        data.boundLogo = bindLogo(entity);
        break;
      case "heading":
      case "paragraph":
        for (const key of ["label", "subtitle", "text"] as const) {
          if (typeof data[key] === "string") {
            data[key] = substitutePlaceholders(
              data[key] as string,
              placeholders
            );
          }
        }
        break;
    }

    if (Array.isArray(element.elements)) {
      element.elements = walkElements(
        element.elements,
        entity,
        selections,
        placeholders,
        timeZone
      );
    }
    out.push(element);
  }
  return out;
}

/**
 * Effective selection for an element: the ephemeral override wins over
 * the persisted data.selection; anything malformed degrades to 'all'.
 */
function resolveSelection(
  elementId: string,
  data: Record<string, unknown>,
  selections: ReportSelections | null
): ElementSelection {
  const override = selections?.[elementId];
  const persisted = data.selection as Partial<ElementSelection> | undefined;
  const chosen = override ?? persisted;
  if (chosen && chosen.mode === "manual") {
    return {
      mode: "manual",
      ids: Array.isArray(chosen.ids) ? chosen.ids.map(String) : [],
    };
  }
  return { mode: "all", ids: [] };
}

function bindJobAttachments(
  entity: JobReportEntityData | null,
  data: Record<string, unknown>,
  selection: ElementSelection
): BoundJobAttachments {
  if (!entity) {
    return { items: [], truncated: false };
  }
  let items: BoundJobAttachmentItem[] = [];
  let truncated = false;
  // Source 1 — job attachments (on unless switched off; per-item
  // selection applies to this source only).
  if (data.includeAttachments !== false) {
    let atts = entity.attachments;
    if (data.imagesOnly === true) {
      atts = atts.filter(item => item.isImage);
    }
    if (selection.mode === "manual") {
      const wanted = new Set(selection.ids);
      const matched = atts.filter(item => wanted.has(String(item.fileId)));
      // Selected ids that no longer exist on the job are silently dropped,
      // but flagged so renderers can surface a note.
      truncated = matched.length < wanted.size;
      atts = matched;
    }
    items = items.concat(atts);
  }
  // Sources 2 & 3 — opt-in, all-or-nothing (already images only).
  if (data.includeFormPhotos === true) {
    items = items.concat(entity.formResultPhotos);
  }
  if (data.includeDrivePhotos === true) {
    items = items.concat(entity.drivePhotos);
  }
  return { items, truncated };
}

/** Rich single-select values ([{value}]) → the bare option value. */
/** A dropdown setting's stored value — a bare string or `[{value}]`. */
export function extractOptionValue(raw: unknown, fallback: string): string {
  if (typeof raw === "string" && raw !== "") return raw;
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (
      first &&
      typeof first === "object" &&
      typeof (first as { value?: unknown }).value === "string"
    ) {
      return (first as { value: string }).value;
    }
  }
  return fallback;
}

/**
 * Preset id → date token pattern (see formatDateTokens).
 *
 * Zero-padded: `03/02/2026`, not `3/2/2026`. An unpadded day and month
 * are ambiguous at a glance in a column of them, and Lifeblood 3.13 asks
 * for one shape across the product. See displayFormat.ts, which is the
 * same decision for the app's own chrome.
 */
const DATE_FORMAT_PRESETS: Record<string, string> = {
  "dmy-time": "DD/MM/YYYY h:mm a",
  dmy: "DD/MM/YYYY",
  ymd: "YYYY-MM-DD",
};
const DEFAULT_DATE_PATTERN = DATE_FORMAT_PRESETS["dmy-time"];

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** The date's wall-clock fields in `timeZone`, or the runtime's zone. */
function zonedFields(
  date: Date,
  timeZone: string | null | undefined
): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-AU", {
        timeZone,
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hourCycle: "h23",
      }).formatToParts(date);
      const get = (type: string) =>
        Number(parts.find(p => p.type === type)?.value ?? NaN);
      const fields = {
        year: get("year"),
        month: get("month"),
        day: get("day"),
        hour: get("hour") % 24,
        minute: get("minute"),
        second: get("second"),
      };
      if (Object.values(fields).every(n => Number.isFinite(n))) return fields;
    } catch {
      // Unknown zone: fall through to the runtime's.
    }
  }
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
  };
}

/**
 * Minimal moment-style token formatter (no locale machinery):
 * dddd ddd · YYYY YY · MMMM MMM MM M · DD D · HH H hh h · mm ss · A a. Anything
 * else in the pattern passes through literally. Wall-clock fields are
 * read in `timeZone` when given (the gateway runs in UTC), else in the
 * runtime's zone.
 */
export function formatDateTokens(
  date: Date,
  pattern: string,
  timeZone?: string | null
): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const f = zonedFields(date, timeZone);
  const h24 = f.hour;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const monthShort = MONTHS_SHORT[f.month - 1];
  const monthLong = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ][f.month - 1];
  // The weekday of the zoned calendar day: a UTC date built from the
  // zoned fields has that day's weekday without any further zone shift.
  const weekday = new Date(Date.UTC(f.year, f.month - 1, f.day)).getUTCDay();
  const weekdayLong = WEEKDAYS_LONG[weekday];
  const tokens: Record<string, string> = {
    dddd: weekdayLong,
    ddd: weekdayLong.slice(0, 3),
    YYYY: String(f.year),
    YY: String(f.year).slice(-2),
    MMMM: monthLong,
    MMM: monthShort,
    MM: pad(f.month),
    M: String(f.month),
    DD: pad(f.day),
    D: String(f.day),
    HH: pad(h24),
    H: String(h24),
    hh: pad(h12),
    h: String(h12),
    mm: pad(f.minute),
    ss: pad(f.second),
    A: h24 < 12 ? "AM" : "PM",
    a: h24 < 12 ? "am" : "pm",
  };
  return pattern.replace(
    /dddd|ddd|YYYY|YY|MMMM|MMM|MM|M|DD|D|HH|H|hh|h|mm|ss|A|a/g,
    t => tokens[t] ?? t
  );
}

const WEEKDAYS_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/** Parse the bridge's date strings (ISO or 'YYYY-MM-DD[ HH:mm:ss]'). */
/** Lenient ISO / MySQL datetime parse; null when unparseable. */
export function parseDateValue(value: string): Date | null {
  let d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    d = new Date(value.replace(" ", "T"));
  }
  return Number.isNaN(d.getTime()) ? null : d;
}

/** The element's effective date pattern (preset or custom tokens). */
export function resolveDatePattern(data: Record<string, unknown>): string {
  const preset = extractOptionValue(data.dateFormat, "dmy-time");
  if (preset === "custom") {
    const custom =
      typeof data.dateFormatCustom === "string"
        ? data.dateFormatCustom.trim()
        : "";
    return custom || DEFAULT_DATE_PATTERN;
  }
  return DATE_FORMAT_PRESETS[preset] ?? DEFAULT_DATE_PATTERN;
}

/** Raw value + whether it's a date, per field id. */
function jobFieldValue(
  job: JobCoreData,
  fieldId: string
): { value: string | null; isDate: boolean } {
  switch (fieldId) {
    case "numberAndName": {
      const number = job.domainId ? `#${job.domainId}` : "";
      const parts = [number, job.title ?? ""].filter(p => p !== "");
      return { value: parts.length ? parts.join(" - ") : null, isDate: false };
    }
    case "name":
      return { value: job.title, isDate: false };
    case "jobNumber":
      return { value: job.domainId ? `#${job.domainId}` : null, isDate: false };
    case "refId":
      return { value: job.refId, isDate: false };
    case "status":
      return { value: job.status, isDate: false };
    case "description":
      return { value: job.description, isDate: false };
    case "leadText":
      return {
        value: richTextToPlainText(job.leadText) || null,
        isDate: false,
      };
    case "workOrderText":
      return {
        value: richTextToPlainText(job.workOrderText) || null,
        isDate: false,
      };
    case "descriptionForCustomer":
      return {
        value: richTextToPlainText(job.descriptionForCustomer) || null,
        isDate: false,
      };
    case "address":
      return { value: job.addressFormatted, isDate: false };
    case "customer":
      return {
        value: job.customerNames.length ? job.customerNames.join(", ") : null,
        isDate: false,
      };
    case "managedProperty":
      return { value: job.managedPropertyName, isDate: false };
    case "siteContact":
      return { value: job.siteContactName, isDate: false };
    case "createdDate":
      return { value: job.createdAt, isDate: true };
    case "dueDate":
      return { value: job.dueAt, isDate: true };
    case "completedDate":
      return { value: job.completedAt, isDate: true };
    default:
      return { value: null, isDate: false };
  }
}

/**
 * One job field's display value, dates formatted per the element's
 * `dateFormat` / `dateFormatCustom` settings. '' when the job has no
 * value (or there is no job). Shared by the job report's `job-field`
 * and the form output's `job-field` / `details-table`.
 */
export function formatJobField(
  job: JobCoreData | null,
  fieldId: string,
  data: Record<string, unknown> = {},
  timeZone?: string | null
): string {
  if (!job) return "";
  const { value, isDate } = jobFieldValue(job, fieldId);
  if (isDate && value) {
    const parsed = parseDateValue(value);
    // A bare date is a calendar day, not an instant: read it as written.
    const zone = /^\d{4}-\d{2}-\d{2}$/.test(value) ? "UTC" : timeZone;
    if (parsed) return formatDateTokens(parsed, resolveDatePattern(data), zone);
  }
  return value ?? "";
}

/**
 * The formatted (HTML) reading of a job's rich-text field, or undefined
 * for every other field and for an empty value. The description falls
 * back to its plain reading when the fetcher sent no `descriptionHtml`:
 * that value is text already (ISM has stripped its tags and decoded its
 * entities), so it is shown as text with its line breaks, never read as
 * markup.
 */
export function jobFieldHtml(
  job: JobCoreData | null,
  fieldId: string
): string | undefined {
  if (!job || !JOB_RICH_TEXT_FIELD_IDS.includes(fieldId)) return undefined;
  let html: string;
  switch (fieldId) {
    case "description":
      html =
        job.descriptionHtml != null
          ? renderRichTextHtml(job.descriptionHtml)
          : plainTextToHtml(job.description ?? "");
      break;
    case "leadText":
      html = renderRichTextHtml(job.leadText);
      break;
    case "workOrderText":
      html = renderRichTextHtml(job.workOrderText);
      break;
    case "descriptionForCustomer":
      html = renderRichTextHtml(job.descriptionForCustomer);
      break;
    default:
      return undefined;
  }
  return html === "" ? undefined : html;
}

/** Bind a `job-field` element against a job core (null = no job). */
export function bindJobFieldForJob(
  job: JobCoreData | null,
  data: Record<string, unknown>,
  timeZone?: string | null
): BoundJobField {
  const fieldId = extractOptionValue(data.field, "name");
  const label = JOB_FIELDS.find(f => f.id === fieldId)?.label ?? fieldId;
  const html = jobFieldHtml(job, fieldId);
  return {
    fieldId,
    label,
    formattedValue: formatJobField(job, fieldId, data, timeZone),
    ...(html !== undefined ? { html } : {}),
  };
}

function bindJobField(
  entity: JobReportEntityData | null,
  data: Record<string, unknown>,
  timeZone?: string | null
): BoundJobField {
  return bindJobFieldForJob(entity ? entity.job : null, data, timeZone);
}

/**
 * Bound job fields for every catalogued field with default formatting —
 * feeds the design canvas's mock context (per-element format config
 * applies in real renders via bindJobField).
 */
export function buildJobFieldBindings(
  entity: JobReportEntityData
): Record<string, BoundJobField> {
  const out: Record<string, BoundJobField> = {};
  for (const field of JOB_FIELDS) {
    out[field.id] = bindJobField(entity, { field: field.id });
  }
  return out;
}

/**
 * Currency display via Intl — same output on gateway (bun), browser and
 * PDF SSR. Falls back to a plain fixed-2 string on a bad code.
 */
export function formatCurrencyAmount(
  amount: number | null | undefined,
  currencyCode?: string | null
): string {
  if (amount == null || Number.isNaN(amount)) return "—";
  try {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: currencyCode || "AUD",
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function bindFinanceList(
  rows: BoundFinanceLine[] | undefined,
  selection?: ElementSelection
): BoundFinanceList {
  let list = rows ?? [];
  if (selection && selection.mode === "manual") {
    const wanted = new Set(selection.ids);
    list = list.filter(row => wanted.has(String(row.id)));
  }
  const total = list.reduce((sum, row) => sum + (row.amount ?? 0), 0);
  return {
    rows: list,
    totalFormatted: formatCurrencyAmount(total),
  };
}

function bindJobNotes(
  entity: JobReportEntityData | null,
  data: Record<string, unknown>,
  selection: ElementSelection
): BoundJobNotes {
  let rows = entity ? entity.notes : [];
  if (data.flaggedOnly === true) {
    rows = rows.filter(row => row.flagged);
  }
  if (data.includeReplies === false) {
    rows = rows.filter(row => !row.isReply);
  }
  if (selection.mode === "manual") {
    const wanted = new Set(selection.ids);
    rows = rows.filter(row => wanted.has(String(row.id)));
  }
  return { rows };
}

/**
 * The element's chat source: "internal" (default) or "external". Stored as
 * a radio-cards rich entry `[{ value }]` by the properties panel, or as a
 * plain string.
 */
export function chatTranscriptSource(
  data: Record<string, unknown>
): "internal" | "external" {
  const raw = data.source;
  const value = Array.isArray(raw)
    ? raw[0] && typeof raw[0] === "object"
      ? String((raw[0] as Record<string, unknown>).value ?? "")
      : String(raw[0] ?? "")
    : typeof raw === "string"
      ? raw
      : "";
  return value === "external" ? "external" : "internal";
}

function bindChatTranscript(
  entity: JobReportEntityData | null,
  data: Record<string, unknown>,
  selection: ElementSelection
): BoundChatTranscript {
  const source = chatTranscriptSource(data);
  const wanted = selection.mode === "manual" ? new Set(selection.ids) : null;
  if (source === "external") {
    // Selection ids are message ids across every channel; a channel with
    // nothing left to show is dropped.
    const groups = (entity?.externalChats ?? [])
      .map(channel => ({
        ...channel,
        rows: wanted
          ? channel.rows.filter(row => wanted.has(String(row.id)))
          : channel.rows,
      }))
      .filter(channel => channel.rows.length > 0);
    return { source, rows: [], groups };
  }
  let rows = entity ? entity.chatMessages : [];
  if (wanted) rows = rows.filter(row => wanted.has(String(row.id)));
  return { source, rows };
}

function bindActionItems(
  entity: JobReportEntityData | null,
  data: Record<string, unknown>,
  selection: ElementSelection
): BoundActionItems {
  let rows = entity ? entity.actionItems : [];
  if (data.includePrivate !== true) {
    rows = rows.filter(row => !row.isPrivate);
  }
  if (data.includeCompleted === false) {
    rows = rows.filter(row => !row.isComplete);
  }
  if (selection.mode === "manual") {
    const wanted = new Set(selection.ids);
    rows = rows.filter(row => wanted.has(String(row.id)));
  }
  return { rows };
}

/** "6h 45m" (or "45m" / "6h"). */
export function formatMinutes(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

function bindWorkLog(entity: JobReportEntityData | null): BoundWorkLog {
  const rows = entity ? entity.workLog : [];
  const totalMinutes = rows.reduce(
    (sum, row) => sum + (row.durationMinutes || 0),
    0
  );
  return { rows, totalMinutes, totalFormatted: formatMinutes(totalMinutes) };
}

function bindCheckIns(
  entity: JobReportEntityData | null,
  data: Record<string, unknown>
): BoundCheckIns {
  let rows = entity ? entity.checkIns : [];
  // In-transit check-ins (travel to site) are shown by default; the
  // designer can hide them via the element's toggle.
  if (data.includeTransit === false) {
    rows = rows.filter(row => !row.isTransit);
  }
  return { rows };
}

function bindLogo(entity: JobReportEntityData | null): BoundLogo {
  return {
    url: entity?.owner.logoUrl ?? null,
    ownerName: entity?.owner.name ?? null,
  };
}

function buildPlaceholders(
  input: RenderEntityReportInput,
  entity: JobReportEntityData | null
): Record<string, string> {
  const placeholders: Record<string, string> = {
    "{{report.name}}": input.reportName ?? "",
    "{{today}}": input.todayFormatted ?? "",
  };
  if (!entity) return placeholders;
  const job = entity.job;
  placeholders["{{job.title}}"] = job.title ?? "";
  placeholders["{{job.refId}}"] = job.refId ?? "";
  placeholders["{{job.status}}"] = job.status ?? "";
  placeholders["{{job.description}}"] = job.description ?? "";
  placeholders["{{job.address}}"] = job.addressFormatted ?? "";
  placeholders["{{job.customer}}"] = job.customerNames.join(", ");
  placeholders["{{job.createdDate}}"] = job.createdAt ?? "";
  placeholders["{{job.dueDate}}"] = job.dueAt ?? "";
  placeholders["{{job.completedDate}}"] = job.completedAt ?? "";
  placeholders["{{client.name}}"] = entity.owner.name ?? "";
  for (const [name, value] of Object.entries(job.customVariables)) {
    placeholders[`{{job.custom.${name}}}`] = value ?? "";
  }
  return placeholders;
}

function substitutePlaceholders(
  value: string,
  placeholders: Record<string, string>
): string {
  if (!Object.keys(placeholders).length) return value;
  let out = value;
  for (const [needle, replacement] of Object.entries(placeholders)) {
    out = out.split(needle).join(replacement ?? "");
  }
  return out;
}
